import { peopleAnalyticsApi } from "@/api/reporting/people-analytics.api.ts";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import EmptyStateServerError from "@/components/empty-state/EmptyStateServerError.tsx";
import { AppFilterType, filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { useDownloadHelper } from "@/hooks/use-download.helper.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { ColumnFilterValueModal } from "@/pages/reporting/employee/people-analytics/components/ColumnFilterValueModal.tsx";
import { AddFilterMenu } from "@/pages/reporting/employee/people-analytics/components/AddFilterMenu.tsx";
import { AddMetricModal } from "@/pages/reporting/employee/people-analytics/components/AddMetricModal.tsx";
import { FilterCard } from "@/pages/reporting/employee/people-analytics/components/FilterCard.tsx";
import { FilterValueModal } from "@/pages/reporting/employee/people-analytics/components/FilterValueModal.tsx";
import {
  PEOPLE_FILTER_FIELDS,
  useFilterCards,
} from "@/pages/reporting/employee/people-analytics/components/useFilterCards.ts";
import { ColumnAggregationHeader } from "@/pages/reporting/employee/people-analytics/components/ColumnAggregationHeader.tsx";
import { ConfigurePanel } from "@/pages/reporting/employee/people-analytics/components/ConfigurePanel.tsx";
import { ConfigureSection } from "@/pages/reporting/employee/people-analytics/components/ConfigureSection.tsx";
import { DateGrainSelector } from "@/pages/reporting/employee/people-analytics/components/DateGrainSelector.tsx";
import { PeopleAnalyticsCacheStatus } from "@/pages/reporting/employee/people-analytics/components/PeopleAnalyticsCacheStatus.tsx";
import { PeopleAnalyticsChart } from "@/pages/reporting/employee/people-analytics/components/PeopleAnalyticsChart.tsx";
import { PeopleAnalyticsEmptyState } from "@/pages/reporting/employee/people-analytics/components/PeopleAnalyticsEmptyState.tsx";
import { TruncatedText } from "@/pages/reporting/employee/people-analytics/components/TruncatedText.tsx";
import {
  buildEmployeeReportSearchParams,
  EMPLOYEE_GROUP_COLUMN_ID,
  encodeMetricsParam,
  formatValuesCell,
  hasLegacyFilterParams,
  isChartableColumn,
  isMoneyAggregation,
  migrateLegacyFilterParams,
  paginateRows,
  parseMetricsParam,
  PEOPLE_ANALYTICS_CURRENCY_PARAM,
  PEOPLE_ANALYTICS_DATE_GRAIN_PARAM,
  PEOPLE_ANALYTICS_GROUP_BY_PARAM,
  PEOPLE_ANALYTICS_METRICS_PARAM,
  PEOPLE_ANALYTICS_PAGE_SIZE,
  PEOPLE_ANALYTICS_WIDGET_PARAM,
  PeopleAnalyticsChartType,
  PeopleAnalyticsView,
  resolveDefaultAggregation,
  setFilterParam,
  TOTAL_EMPLOYEES_SERIES_ID,
  widgetToSearchParams,
} from "@/pages/reporting/employee/people-analytics/people-analytics.types.ts";
import { GroupBySelector } from "@/pages/reporting/employee/people-analytics/components/GroupBySelector.tsx";
import { MetricCard } from "@/pages/reporting/employee/people-analytics/components/MetricCard.tsx";
import { PeriodSelector } from "@/pages/reporting/employee/people-analytics/components/PeriodSelector.tsx";
import { VisualisationSelector } from "@/pages/reporting/employee/people-analytics/components/VisualisationSelector.tsx";
import { SaveWidgetModal } from "@/pages/reporting/employee/people-analytics/components/SaveWidgetModal.tsx";
import {
  useCreatePeopleAnalyticsWidget,
  useDeletePeopleAnalyticsWidget,
  usePeopleAnalyticsColumns,
  usePeopleAnalyticsRows,
  usePeopleAnalyticsWidgets,
  useUpdatePeopleAnalyticsWidget,
} from "@/queries/reporting/use-people-analytics.query.ts";
import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import { useEmployeesByIds } from "@/queries/employees/use-employees.query.ts";
import { PreviewEmployeesModal } from "@/pages/employees/employee/widgets/PreviewEmployeesModal.tsx";
import { useDateFilterFormat } from "@/components/date-filter/date-filter-presets.ts";
import { formatColumnFilterValue } from "@/pages/employees/index/employee-column-filter-summary.ts";
import {
  createEmployeeColumnFilter,
  getClearedColumnFilters,
  getDisplayedColumnFilters,
  getFilterColumnTableTitles,
  getFilterFieldTitle,
} from "@/pages/employees/index/employee-column-filters-modal.helper.ts";
import {
  LIFECYCLE_STATUS_COLUMN,
  useEmployeeColumnFilters,
} from "@/pages/employees/index/use-employee-column-filters.ts";
import { useOrganizationViewFilter } from "@/queries/settings/use-organization.query.ts";
import { useCurrencies } from "@/queries/currencies.query.ts";
import { useEmployeeFilterCategories } from "@/pages/employees/index/use-employee-filter-categories.ts";
import { AnalyticReportTypes } from "@/types/analytics/analytics.types.ts";
import {
  ApiEmployeeColumn,
  ApiEmployeeColumnFilter,
  EmployeeColumnFilterColumn,
} from "@/types/employees/employee-column.types.ts";
import { ColumnConfig, ColumnDefinition } from "@/types/column.types.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import {
  ApiPeopleAnalyticsAggregation,
  ApiPeopleAnalyticsCellValue,
  ApiPeopleAnalyticsColumn,
  ApiPeopleAnalyticsColumnGroup,
  ApiPeopleAnalyticsColumnSource,
  ApiPeopleAnalyticsDateGrain,
  ApiPeopleAnalyticsEmployeeFilter,
  ApiPeopleAnalyticsFormat,
  ApiPeopleAnalyticsRow,
  ApiPeopleAnalyticsTimeScope,
  ApiPeopleAnalyticsTimeScopeMode,
  ApiPeopleAnalyticsValueType,
  ApiPeopleAnalyticsWarningCode,
  ApiPeopleAnalyticsWidgetSaveInput,
  ApiPeopleAnalyticsWidgetType,
} from "@/types/reporting/people-analytics.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiEmployeeLifecycleStatus } from "@/types/employees/employee.types.ts";
import {
  Box,
  Button,
  ConfirmationModal,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Input,
  Page,
  PaginationBlock,
  SplitButton,
  Stack,
  Table,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useModals,
  useToasts,
} from "@skailer-com/uikit";
import { ColumnDef } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";

const GROUP_COLUMN_ID = "__group";
const previewLifecycleStatuses = Object.values(ApiEmployeeLifecycleStatus);

function readTimeScope(searchParams: URLSearchParams): ApiPeopleAnalyticsTimeScope {
  const mode = searchParams.get("scope") as ApiPeopleAnalyticsTimeScopeMode | null;
  if (mode === ApiPeopleAnalyticsTimeScopeMode.as_of) {
    return { mode, date_at: searchParams.get("scope_date") };
  }
  if (mode === ApiPeopleAnalyticsTimeScopeMode.period) {
    return {
      mode,
      date_from: searchParams.get("scope_from"),
      date_to: searchParams.get("scope_to"),
    };
  }
  return { mode: ApiPeopleAnalyticsTimeScopeMode.current };
}

// Незаполненный срез бэк отбивает 422, поэтому до выбора всех дат запрашиваем «сейчас»
function isTimeScopeComplete(timeScope: ApiPeopleAnalyticsTimeScope): boolean {
  if (timeScope.mode === ApiPeopleAnalyticsTimeScopeMode.as_of) return !!timeScope.date_at;
  if (timeScope.mode === ApiPeopleAnalyticsTimeScopeMode.period) {
    return !!timeScope.date_from && !!timeScope.date_to;
  }
  return true;
}

interface PeopleAnalyticsProps {
  view: PeopleAnalyticsView;
}

export function PeopleAnalytics({ view }: PeopleAnalyticsProps) {
  const { t } = useTranslation();
  const { canIEdit } = usePermissions();
  const { renderDate, renderCurrency } = useFormatter();
  const { downloadFile, isExporting } = useDownloadHelper();
  const modalsService = useModals();
  const { showInfo } = useToasts();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const chartType =
    searchParams.get("chart") === PeopleAnalyticsChartType.line
      ? PeopleAnalyticsChartType.line
      : PeopleAnalyticsChartType.bar;
  const isChart = view === PeopleAnalyticsView.chart;
  const { visibleColumnIds, aggregations } = useMemo(() => {
    const parsed = parseMetricsParam(searchParams.get(PEOPLE_ANALYTICS_METRICS_PARAM));
    return parsed
      ? { visibleColumnIds: parsed.columnIds, aggregations: parsed.aggregations }
      : { visibleColumnIds: [] as string[], aggregations: {} };
  }, [searchParams]);
  const groupByColumnId = searchParams.get("group_by") || null;
  const isEmployeeGrouping = groupByColumnId === EMPLOYEE_GROUP_COLUMN_ID;
  const [groupByOpen, setGroupByOpen] = useState(false);
  const [sort, setSort] = useState<{ columnId: string; direction: "asc" | "desc" } | null>(null);

  const dateGrainParam = searchParams.get(PEOPLE_ANALYTICS_DATE_GRAIN_PARAM);
  const dateGrain = Object.values(ApiPeopleAnalyticsDateGrain).includes(
    dateGrainParam as ApiPeopleAnalyticsDateGrain
  )
    ? (dateGrainParam as ApiPeopleAnalyticsDateGrain)
    : null;
  const handleDateGrainChange = useCallback(
    (nextGrain: ApiPeopleAnalyticsDateGrain | null) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (nextGrain) next.set(PEOPLE_ANALYTICS_DATE_GRAIN_PARAM, nextGrain);
          else next.delete(PEOPLE_ANALYTICS_DATE_GRAIN_PARAM);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const selectedCurrency = searchParams.get(PEOPLE_ANALYTICS_CURRENCY_PARAM);
  const [currencySearch, setCurrencySearch] = useState("");
  const { data: currencies } = useCurrencies();
  const setCurrency = useCallback(
    (value: string) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set(PEOPLE_ANALYTICS_CURRENCY_PARAM, value);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const widgets = usePeopleAnalyticsWidgets(canIEdit(ApiPermission.reporting_people_analytics));
  const currentWidgetParam = searchParams.get(PEOPLE_ANALYTICS_WIDGET_PARAM);
  const currentWidgetId = currentWidgetParam ? Number(currentWidgetParam) : null;
  const currentWidget =
    currentWidgetId !== null
      ? (widgets.data?.find((widget) => widget.id === currentWidgetId) ?? null)
      : null;
  const canUpdateWidget = !!currentWidget && currentWidget.is_own;

  // Открыли по ссылке `?widget=id`: разворачиваем сохранённый виджет в параметры билдера.
  // Если параметры уже есть (гидратировали ранее или пришла расшаренная ссылка) — не трогаем.
  const hasMetricsParam = searchParams.has(PEOPLE_ANALYTICS_METRICS_PARAM);
  useEffect(() => {
    if (currentWidgetId === null || hasMetricsParam || !currentWidget) return;
    setSearchParams(widgetToSearchParams(currentWidget), { replace: true });
  }, [currentWidgetId, hasMetricsParam, currentWidget, setSearchParams]);

  const setMetrics = useCallback(
    (ids: string[], aggs: Record<string, ApiPeopleAnalyticsAggregation>) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set(
            PEOPLE_ANALYTICS_METRICS_PARAM,
            encodeMetricsParam(ids.map((id) => ({ column_id: id, aggregation: aggs[id] })))
          );
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const timeScope = useMemo(() => readTimeScope(searchParams), [searchParams]);
  const handleTimeScopeChange = useCallback(
    (nextTimeScope: ApiPeopleAnalyticsTimeScope) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete("scope_date");
          next.delete("scope_from");
          next.delete("scope_to");
          if (nextTimeScope.mode === ApiPeopleAnalyticsTimeScopeMode.current) {
            next.delete("scope");
            return next;
          }
          next.set("scope", nextTimeScope.mode);
          if (nextTimeScope.date_at) next.set("scope_date", nextTimeScope.date_at);
          if (nextTimeScope.date_from) next.set("scope_from", nextTimeScope.date_from);
          if (nextTimeScope.date_to) next.set("scope_to", nextTimeScope.date_to);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleGroupByChange = useCallback(
    (columnId: string | null) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set("group_by", columnId ?? "");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const library = filtersLibrary();
  const filterItems = useMemo(() => [library.text, library.team_ids], [library]);
  const {
    filterCategories: columnFilterCategories,
    columnByKey: employeeColumnByKey,
    isLoading: isEmployeeCatalogLoading,
  } = useEmployeeFilterCategories();
  const columnFilters = useEmployeeColumnFilters(employeeColumnByKey);

  const hasLegacyFilters = hasLegacyFilterParams(searchParams);
  useEffect(() => {
    if (!hasLegacyFilters || isEmployeeCatalogLoading) return;
    setSearchParams(
      migrateLegacyFilterParams(
        searchParams,
        columnFilters.filters,
        new Set(employeeColumnByKey.keys())
      ),
      { replace: true }
    );
  }, [
    hasLegacyFilters,
    isEmployeeCatalogLoading,
    searchParams,
    columnFilters.filters,
    employeeColumnByKey,
    setSearchParams,
  ]);

  const lifecycleColumn = employeeColumnByKey.get(LIFECYCLE_STATUS_COLUMN);
  const { data: organizationViewFilter } = useOrganizationViewFilter(
    Boolean(lifecycleColumn?.is_filterable)
  );
  const lifecycleStatusDefaults = organizationViewFilter?.employee_lifecycle_statuses;
  const displayedColumnFilters = useMemo(
    () =>
      getDisplayedColumnFilters(
        columnFilters.activeFilters,
        lifecycleColumn,
        lifecycleStatusDefaults
      ),
    [columnFilters.activeFilters, lifecycleColumn, lifecycleStatusDefaults]
  );

  const { filter: activeFilter, isFilterReady } = useAppFilter<ApiPeopleAnalyticsEmployeeFilter>({
    filters: filterItems,
    source: AnalyticReportTypes.peopleAnalytics,
  });
  const filter = useMemo<ApiPeopleAnalyticsEmployeeFilter>(
    () => ({
      ...activeFilter.filter,
      ...columnFilters.requestFilters,
      employee_lifecycle_statuses: columnFilters.employee_lifecycle_statuses,
    }),
    [activeFilter.filter, columnFilters.requestFilters, columnFilters.employee_lifecycle_statuses]
  );

  const openFilterField = useCallback(
    (type: AppFilterType) => {
      const item = filterItems.find((filterItem) => filterItem.type === type);
      const fieldConfig = PEOPLE_FILTER_FIELDS.find((field) => field.type === type);
      if (!item || !fieldConfig) return;
      modalsService.openModal(FilterValueModal, {
        field: item,
        label: t(fieldConfig.labelKey),
        currentFilter: activeFilter,
        filters: filterItems,
      });
    },
    [filterItems, activeFilter, modalsService, t]
  );

  const removeFilterField = useCallback(
    (field: { type: AppFilterType; name: keyof ApiPeopleAnalyticsEmployeeFilter }) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          setFilterParam(next, field.name, undefined);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const { formatRange } = useDateFilterFormat();
  const columnFilterItems = useMemo(
    () =>
      columnFilterCategories.flatMap((category) =>
        category.columns.map((column) => ({ column, categoryTitle: category.title }))
      ),
    [columnFilterCategories]
  );
  const tableTitleByColumn = useMemo(
    () => getFilterColumnTableTitles(columnFilterCategories),
    [columnFilterCategories]
  );
  const getTableFilterLabel = useCallback(
    (column: ApiEmployeeColumn) =>
      getFilterFieldTitle(column.title, tableTitleByColumn.get(column.id)),
    [tableTitleByColumn]
  );

  const openTableFilterField = useCallback(
    (columnId: string) => {
      const column = employeeColumnByKey.get(columnId);
      if (!column) return;
      const { filters, setFilters } = columnFilters;
      modalsService.openModal(ColumnFilterValueModal, {
        column,
        label: getTableFilterLabel(column),
        filter:
          displayedColumnFilters.find((item) => item.field === columnId) ??
          createEmployeeColumnFilter(column),
        onApply: (next: ApiEmployeeColumnFilter) =>
          setFilters([...filters.filter((item) => item.field !== columnId), next]),
      });
    },
    [employeeColumnByKey, columnFilters, displayedColumnFilters, getTableFilterLabel, modalsService]
  );

  const removeTableFilterField = useCallback(
    (columnId: string) => {
      const rest = columnFilters.filters.filter((item) => item.field !== columnId);
      columnFilters.setFilters(
        columnId === LIFECYCLE_STATUS_COLUMN
          ? [...rest, ...getClearedColumnFilters(lifecycleColumn, lifecycleStatusDefaults)]
          : rest
      );
    },
    [columnFilters, lifecycleColumn, lifecycleStatusDefaults]
  );

  const tableFilterCards = useMemo(
    () =>
      displayedColumnFilters.flatMap((item) => {
        const column = employeeColumnByKey.get(item.field);
        if (!column) return [];
        const value = formatColumnFilterValue(item, column, t, formatRange);
        return value ? [{ id: item.field, label: getTableFilterLabel(column), value }] : [];
      }),
    [displayedColumnFilters, employeeColumnByKey, getTableFilterLabel, formatRange, t]
  );

  const filterCards = useFilterCards(filter);
  const availableFilterFields = useMemo(
    () => [
      ...PEOPLE_FILTER_FIELDS.filter(
        (field) => !filterCards.some((card) => card.type === field.type)
      ).map((field) => ({ id: field.type as string, label: t(field.labelKey) })),
      ...columnFilterItems
        .filter(({ column }) => !tableFilterCards.some((card) => card.id === column.id))
        .map(({ column, categoryTitle }) => ({
          id: column.id,
          label: getTableFilterLabel(column),
          shortLabel: column.title,
          submenu: categoryTitle,
          group: (column as EmployeeColumnFilterColumn).groupTitle,
        })),
    ],
    [filterCards, columnFilterItems, tableFilterCards, getTableFilterLabel, t]
  );

  const handleSelectFilterField = useCallback(
    (id: string) => {
      if (employeeColumnByKey.has(id)) openTableFilterField(id);
      else openFilterField(id as AppFilterType);
    },
    [employeeColumnByKey, openTableFilterField, openFilterField]
  );

  const catalog = usePeopleAnalyticsColumns();
  const catalogColumns = useMemo(
    () => (catalog.data?.groups ?? []).flatMap((group) => group.columns),
    [catalog.data]
  );

  const resolveAggregation = useCallback(
    (columnId: string) =>
      aggregations[columnId] ??
      resolveDefaultAggregation(
        catalogColumns.find((column) => column.id === columnId),
        isEmployeeGrouping
      ),
    [aggregations, catalogColumns, isEmployeeGrouping]
  );

  // «Без группировки» хранится как пустой group_by, чтобы отличаться от входа без параметров
  useEffect(() => {
    if (!catalog.isSuccess) return;
    const hasReportParams = [
      PEOPLE_ANALYTICS_GROUP_BY_PARAM,
      PEOPLE_ANALYTICS_METRICS_PARAM,
      PEOPLE_ANALYTICS_WIDGET_PARAM,
    ].some((param) => searchParams.has(param));
    if (hasReportParams) return;
    const preset = buildEmployeeReportSearchParams(catalogColumns);
    if (!preset) return;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        preset.forEach((value, key) => next.set(key, value));
        return next;
      },
      { replace: true }
    );
  }, [catalog.isSuccess, catalogColumns, searchParams, setSearchParams]);

  // Грануляция применима только к колонке-дате; на остальных бэк отбивает её 422
  const isGroupByDate = useMemo(
    () =>
      catalogColumns.some(
        (column) =>
          column.id === groupByColumnId && column.value_type === ApiPeopleAnalyticsValueType.date
      ),
    [catalogColumns, groupByColumnId]
  );

  const columnConfig = useMemo<ColumnConfig>(
    () => ({
      type: "people_analytics_columns",
      categories: [
        ...(catalog.data?.groups ?? []).map((group) => ({
          id: group.group,
          title: group.title,
          columns: group.columns.map((column) => ({
            id: column.id,
            // У колонок таблиц бэкенд уже добавляет название таблицы.
            title:
              column.source_type !== ApiPeopleAnalyticsColumnSource.custom_table &&
              [
                ApiPeopleAnalyticsColumnGroup.employee_position,
                ApiPeopleAnalyticsColumnGroup.employee_employment,
                ApiPeopleAnalyticsColumnGroup.employee_compensation,
                ApiPeopleAnalyticsColumnGroup.employee_additional_compensation,
                ApiPeopleAnalyticsColumnGroup.employee_termination,
              ].includes(group.group)
                ? `${group.title} — ${column.title}`
                : column.title,
          })),
        })),
      ],
    }),
    [catalog.data]
  );

  const metricTitles = useMemo(
    () =>
      new Map(
        columnConfig.categories.flatMap((category) =>
          category.columns.map((column) => [column.id, column.title] as const)
        )
      ),
    [columnConfig]
  );

  const rowsInput = useMemo(
    () => ({
      // Вычисляемая метрика уже агрегат: агрегацию у неё бэк отбивает 422
      columns: visibleColumnIds.map((columnId) => ({
        column_id: columnId,
        aggregation: catalogColumns.find((column) => column.id === columnId)?.aggregations.length
          ? resolveAggregation(columnId)
          : undefined,
      })),
      filter,
      group_by_column_id: groupByColumnId,
      group_by_date_grain: isGroupByDate ? dateGrain : null,
      time_scope: isTimeScopeComplete(timeScope)
        ? timeScope
        : { mode: ApiPeopleAnalyticsTimeScopeMode.current },
      compensation: selectedCurrency ? { currency: selectedCurrency } : undefined,
    }),
    [
      visibleColumnIds,
      catalogColumns,
      resolveAggregation,
      filter,
      groupByColumnId,
      isGroupByDate,
      dateGrain,
      timeScope,
      selectedCurrency,
    ]
  );

  const rows = usePeopleAnalyticsRows(
    rowsInput,
    catalog.isSuccess && isFilterReady && !hasLegacyFilters && !!groupByColumnId
  );

  const responseColumnIds = useMemo(
    () => (rows.data?.columns ?? []).map((column) => column.id),
    [rows.data]
  );

  const sortedRows = useMemo(() => {
    const source = rows.data?.rows ?? [];
    if (!sort) return source;
    const valueIndex =
      sort.columnId === GROUP_COLUMN_ID ? -1 : responseColumnIds.indexOf(sort.columnId);
    if (sort.columnId !== GROUP_COLUMN_ID && valueIndex < 0) return source;
    const getValue = (row: ApiPeopleAnalyticsRow): ApiPeopleAnalyticsCellValue =>
      sort.columnId === GROUP_COLUMN_ID ? row.group_label : row.values[valueIndex];
    const isEmpty = (value: ApiPeopleAnalyticsCellValue) =>
      value === null || value === undefined || value === "";
    // Деньги приходят строкой с хвостом разной длины: текстом «0.07» встаёт после «0.45»
    const toNumber = (value: ApiPeopleAnalyticsCellValue) =>
      typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
    return [...source].sort((rowA, rowB) => {
      const a = getValue(rowA);
      const b = getValue(rowB);
      if (isEmpty(a) || isEmpty(b)) {
        if (isEmpty(a) && isEmpty(b)) return 0;
        return isEmpty(a) ? 1 : -1;
      }
      const numberA = toNumber(a);
      const numberB = toNumber(b);
      let base: number;
      if (!isNaN(numberA) && !isNaN(numberB)) base = numberA - numberB;
      else if (a instanceof Date && b instanceof Date) base = a.getTime() - b.getTime();
      else base = String(a).localeCompare(String(b), undefined, { numeric: true });
      return sort.direction === "asc" ? base : -base;
    });
  }, [rows.data, sort, responseColumnIds]);

  const pageSignature = useMemo(() => JSON.stringify([rowsInput, sort]), [rowsInput, sort]);
  const [pageState, setPageState] = useState({ page: 1, signature: "" });
  const currentPage = pageState.signature === pageSignature ? pageState.page : 1;
  const pagedRows = useMemo(
    () => paginateRows(sortedRows, currentPage, PEOPLE_ANALYTICS_PAGE_SIZE),
    [sortedRows, currentPage]
  );
  const pageEmployeeIds = useMemo(
    () => (isEmployeeGrouping ? pagedRows.items.flatMap((row) => row.employee_ids) : []),
    [isEmployeeGrouping, pagedRows.items]
  );
  const { data: pageEmployees } = useEmployeesByIds(pageEmployeeIds);
  const employeeById = useMemo(
    () => new Map((pageEmployees ?? []).map((employee) => [employee.id, employee])),
    [pageEmployees]
  );
  const handlePageChange = useCallback(
    (page: number) => setPageState({ page, signature: pageSignature }),
    [pageSignature]
  );

  const warningHintByColumnId = useMemo(() => {
    const hints: Record<string, string> = {};
    // У денежной колонки бывают сразу missing_fx_rate и missing_currency
    const addHint = (columnId: string, text: string) => {
      hints[columnId] = hints[columnId] ? `${hints[columnId]}\n${text}` : text;
    };
    (rows.data?.warnings ?? []).forEach((warning) => {
      if (warning.code === ApiPeopleAnalyticsWarningCode.no_history) {
        addHint(warning.column_id, t("reporting:people_analytics_warning_no_history"));
      }
      if (warning.code === ApiPeopleAnalyticsWarningCode.history_access_denied) {
        addHint(warning.column_id, t("reporting:people_analytics_warning_history_access_denied"));
      }
      if (warning.code === ApiPeopleAnalyticsWarningCode.metric_requires_period) {
        addHint(warning.column_id, t("reporting:people_analytics_warning_metric_requires_period"));
      }
      if (warning.code === ApiPeopleAnalyticsWarningCode.missing_fx_rate) {
        addHint(warning.column_id, t("reporting:people_analytics_warning_missing_fx_rate"));
      }
      if (warning.code === ApiPeopleAnalyticsWarningCode.missing_currency) {
        addHint(warning.column_id, t("reporting:people_analytics_warning_missing_currency"));
      }
    });
    return hints;
  }, [rows.data, t]);

  const columnDefinitions = useMemo<Record<string, ColumnDef<ApiPeopleAnalyticsRow>>>(() => {
    const currency = rows.data?.currency ?? null;
    const renderCell = (value: ApiPeopleAnalyticsCellValue, column: ApiPeopleAnalyticsColumn) => {
      if (value === null || value === undefined || value === "") return "-";
      const aggregation = resolveAggregation(column.id);
      if (aggregation === ApiPeopleAnalyticsAggregation.values) {
        return formatValuesCell(String(value), column.value_type, {
          renderDate,
          renderMoney: (part) => renderCurrency(part, currency),
          yes: t("common:yes"),
          no: t("common:no"),
        });
      }
      if (isMoneyAggregation(column, aggregation)) {
        return renderCurrency(String(value), currency);
      }
      const isCountAggregation =
        aggregation === ApiPeopleAnalyticsAggregation.count ||
        aggregation === ApiPeopleAnalyticsAggregation.count_distinct;
      if (!isCountAggregation && column.value_type === ApiPeopleAnalyticsValueType.date) {
        const date = value instanceof Date ? value : new Date(String(value));
        return isNaN(date.getTime()) ? "-" : renderDate(date);
      }
      if (!isCountAggregation && column.value_type === ApiPeopleAnalyticsValueType.bool) {
        return value ? t("common:yes") : t("common:no");
      }
      return String(value);
    };

    const definitions: Record<string, ColumnDef<ApiPeopleAnalyticsRow>> = {
      [GROUP_COLUMN_ID]: {
        id: GROUP_COLUMN_ID,
        header: isEmployeeGrouping
          ? t("reporting:people_analytics_group_by_employee")
          : (rows.data?.group_by_column?.title ?? t("reporting:people_analytics_row_total")),
        cell: (cell) => {
          const employee = isEmployeeGrouping
            ? employeeById.get(cell.row.original.employee_ids[0])
            : undefined;
          if (employee) {
            return (
              <EmployeeItem employee={employee} size="md" showPosition={false} enablePreview />
            );
          }
          return (
            <Text className="font-semibold">
              {rows.data?.group_by_column
                ? cell.row.original.group_label || t("reporting:people_analytics_group_empty")
                : t("reporting:people_analytics_row_total")}
            </Text>
          );
        },
        ...ColumnSizes.employee,
      },
    };

    const resolveMetricColumnSize = (column: ApiPeopleAnalyticsColumn) => {
      if (resolveAggregation(column.id) !== ApiPeopleAnalyticsAggregation.values) {
        return ColumnSizes.standard;
      }
      if (column.value_type === ApiPeopleAnalyticsValueType.date) return ColumnSizes.date;
      if (column.value_type === ApiPeopleAnalyticsValueType.bool) return ColumnSizes.narrow;
      return ColumnSizes.standard;
    };

    catalogColumns.forEach((column) => {
      definitions[column.id] = {
        id: column.id,
        ...resolveMetricColumnSize(column),
        header: () => (
          <ColumnAggregationHeader
            column={column}
            warningHint={warningHintByColumnId[column.id]}
            aggregation={resolveAggregation(column.id)}
            onChange={(aggregation) =>
              setMetrics(visibleColumnIds, { ...aggregations, [column.id]: aggregation })
            }
            sortDirection={sort?.columnId === column.id ? sort.direction : null}
            onSort={(direction) => setSort(direction ? { columnId: column.id, direction } : null)}
          />
        ),
        cell: (cell) => {
          // Значение ищется по id: порядок колонок в таблице и в ответе расходится на рендер до ответа
          const valueIndex = responseColumnIds.indexOf(column.id);
          if (valueIndex < 0) return <Text className="text-secondary">-</Text>;
          const content = renderCell(cell.row.original.values[valueIndex], column);
          if (resolveAggregation(column.id) === ApiPeopleAnalyticsAggregation.values) {
            return content === "-" ? (
              <Text className="text-secondary">-</Text>
            ) : (
              <TruncatedText text={content} />
            );
          }
          return <Text className="text-secondary">{content}</Text>;
        },
      };
    });

    return definitions;
  }, [
    catalogColumns,
    responseColumnIds,
    warningHintByColumnId,
    rows.data,
    resolveAggregation,
    setMetrics,
    visibleColumnIds,
    aggregations,
    t,
    renderDate,
    renderCurrency,
    sort,
    isEmployeeGrouping,
    employeeById,
  ]);

  const columns = useMemo(
    () =>
      [GROUP_COLUMN_ID, ...visibleColumnIds]
        .map((columnId) => columnDefinitions[columnId])
        .filter(Boolean),
    [visibleColumnIds, columnDefinitions]
  );

  const visibleCatalogColumns = useMemo(
    () =>
      visibleColumnIds
        .map((columnId) => catalogColumns.find((column) => column.id === columnId))
        .filter((column): column is ApiPeopleAnalyticsColumn => column !== undefined),
    [visibleColumnIds, catalogColumns]
  );
  const chartableColumns = useMemo(
    () =>
      visibleCatalogColumns.filter((column) =>
        isChartableColumn(column, resolveAggregation(column.id))
      ),
    [visibleCatalogColumns, resolveAggregation]
  );
  const moneyColumnIds = useMemo(
    () =>
      visibleCatalogColumns
        .filter((column) => isMoneyAggregation(column, resolveAggregation(column.id)))
        .map((column) => column.id),
    [visibleCatalogColumns, resolveAggregation]
  );
  const hasMoneyMetric = visibleCatalogColumns.some(
    (column) => column.value_type === ApiPeopleAnalyticsValueType.money
  );

  const currencyOptions = useMemo(
    () =>
      currencies?.map((currency) => ({
        id: currency.code,
        title: `${currency.code} (${currency.symbol_native})`,
      })) ?? [],
    [currencies]
  );
  const shownCurrency = selectedCurrency ?? rows.data?.currency ?? null;
  const shownCurrencyTitle =
    currencyOptions.find((currency) => currency.id === shownCurrency)?.title ??
    shownCurrency ??
    t("common:currency");

  const seriesParam = searchParams.get("series");
  const seriesColumnIds = useMemo(() => {
    if (seriesParam !== null) return seriesParam.split(",").filter(Boolean);
    const chartable = chartableColumns.map((column) => column.id);
    return chartable.length > 0 ? chartable : [TOTAL_EMPLOYEES_SERIES_ID];
  }, [seriesParam, chartableColumns]);

  const setSeries = useCallback(
    (ids: string[]) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set("series", ids.join(","));
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleSelectTable = useCallback(() => {
    navigate(`/reporting/employee/people-analytics?${searchParams.toString()}`);
  }, [navigate, searchParams]);

  const handleSelectChart = useCallback(
    (type: PeopleAnalyticsChartType) => {
      const next = new URLSearchParams(searchParams);
      next.set("chart", type);
      navigate(`/reporting/employee/people-analytics/chart?${next.toString()}`);
    },
    [navigate, searchParams]
  );

  const openGroupEmployees = useCallback(
    (row: ApiPeopleAnalyticsRow | undefined) => {
      if (!row?.employee_ids.length) return;
      modalsService.openModal(PreviewEmployeesModal, {
        filter: {
          page_number: 1,
          employee_ids: row.employee_ids,
          employee_lifecycle_statuses: previewLifecycleStatuses,
        },
        addColumns: [],
        modalTitle: row.group_label ?? t("reporting:people_analytics_group_empty"),
        source: AnalyticReportTypes.peopleAnalytics,
      });
    },
    [modalsService, t]
  );

  const handleAddMetric = useCallback(() => {
    modalsService.openModal(AddMetricModal, {
      columnConfig,
      visibleColumns: visibleColumnIds,
      onSubmit: (updatedColumns: ColumnDefinition[]) =>
        setMetrics(
          updatedColumns.map((column) => column.id),
          aggregations
        ),
    });
  }, [modalsService, columnConfig, visibleColumnIds, aggregations, setMetrics]);

  const createWidget = useCreatePeopleAnalyticsWidget();
  const updateWidget = useUpdatePeopleAnalyticsWidget();
  const deleteWidget = useDeletePeopleAnalyticsWidget();

  const buildSaveInput = useCallback(
    (title: string): ApiPeopleAnalyticsWidgetSaveInput => {
      const widgetType = !groupByColumnId
        ? ApiPeopleAnalyticsWidgetType.kpi
        : !isChart
          ? ApiPeopleAnalyticsWidgetType.table
          : chartType === PeopleAnalyticsChartType.line
            ? ApiPeopleAnalyticsWidgetType.line_chart
            : ApiPeopleAnalyticsWidgetType.bar_chart;
      return {
        title,
        // Ключ серии — колонка с агрегацией: одна колонка бывает в запросе с разными агрегациями
        metrics: rowsInput.columns.map((column) => {
          const key = column.aggregation
            ? `${column.column_id}|${column.aggregation}`
            : column.column_id;
          // Цвет и event_date проставляются на дашборде; экран отчёта их не трогает — переносим как есть
          const source = currentWidget?.metrics.find((metric) => metric.key === key);
          return {
            key,
            column_id: column.column_id,
            aggregation: column.aggregation ?? null,
            color: source?.color ?? null,
            event_date_column_id: source?.event_date_column_id ?? null,
          };
        }),
        filter,
        group_by_column_id: groupByColumnId,
        date_grain: dateGrain ?? ApiPeopleAnalyticsDateGrain.month,
        // Поля, которых нет на экране отчёта, берём из исходного виджета, чтобы PUT их не обнулил
        visualization: currentWidget
          ? { ...currentWidget.visualization, widget_type: widgetType }
          : { widget_type: widgetType },
        dashboard_rules: currentWidget?.dashboard_rules,
        compensation:
          currentWidget?.compensation || selectedCurrency
            ? { ...currentWidget?.compensation, currency: selectedCurrency }
            : null,
      };
    },
    [
      groupByColumnId,
      isChart,
      chartType,
      rowsInput,
      filter,
      dateGrain,
      currentWidget,
      selectedCurrency,
    ]
  );

  const handleSaveAs = useCallback(() => {
    modalsService.openModal(SaveWidgetModal, {
      onSave: async (title: string) => {
        const created = await createWidget.mutateAsync(buildSaveInput(title));
        setSearchParams(
          (current) => {
            const next = new URLSearchParams(current);
            next.set(PEOPLE_ANALYTICS_WIDGET_PARAM, String(created.id));
            return next;
          },
          { replace: true }
        );
      },
    });
  }, [modalsService, createWidget, buildSaveInput, setSearchParams]);

  const handleUpdateWidget = useCallback(() => {
    if (!currentWidget) return;
    updateWidget.mutate(
      { widgetId: currentWidget.id, input: buildSaveInput(currentWidget.title) },
      { onSuccess: () => showInfo({ title: t("reporting:people_analytics_widget_updated") }) }
    );
  }, [currentWidget, updateWidget, buildSaveInput, showInfo, t]);

  const handleRenameWidget = useCallback(() => {
    if (!currentWidget) return;
    modalsService.openModal(SaveWidgetModal, {
      defaultTitle: currentWidget.title,
      heading: t("reporting:people_analytics_rename_report"),
      onSave: async (title: string) => {
        await updateWidget.mutateAsync({
          widgetId: currentWidget.id,
          input: buildSaveInput(title),
        });
      },
    });
  }, [currentWidget, modalsService, updateWidget, buildSaveInput, t]);

  const handleDeleteWidget = useCallback(() => {
    if (!currentWidget) return;
    modalsService.openModal(ConfirmationModal, {
      question: t("reporting:people_analytics_delete_report"),
      text: t("reporting:people_analytics_delete_confirm", { title: currentWidget.title }),
      acceptTitle: t("common:delete"),
      onAccept: async () => {
        await deleteWidget.mutateAsync(currentWidget.id);
        navigate("/reporting/employee/people-analytics");
      },
    });
  }, [currentWidget, modalsService, deleteWidget, navigate, t]);

  const handleExportClick = useCallback(() => {
    downloadFile({
      apiCall: () =>
        peopleAnalyticsApi.rowsExport({ ...rowsInput, format: ApiPeopleAnalyticsFormat.xlsx }),
    });
  }, [downloadFile, rowsInput]);

  const hasValuesMetric = useMemo(
    () =>
      rowsInput.columns.some(
        (column) => column.aggregation === ApiPeopleAnalyticsAggregation.values
      ),
    [rowsInput.columns]
  );
  // `values` отдаёт текст: бэк принимает её только в таблице, у графиков и KPI — 422
  const isTableWidget = Boolean(groupByColumnId) && !isChart;
  const blockValuesSave = hasValuesMetric && !isTableWidget;
  const isSaveDisabled = rowsInput.columns.length === 0 || blockValuesSave;
  const saveBlockedHint = blockValuesSave
    ? t("reporting:people_analytics_save_blocked_values")
    : undefined;

  const groupByColumn = catalogColumns.find((column) => column.id === groupByColumnId);
  const headerTitle = isEmployeeGrouping
    ? t("reporting:people_analytics_employee_report")
    : groupByColumn
      ? t("reporting:people_analytics_report_title", { group: groupByColumn.title })
      : t("reporting:people_analytics_new_report");

  useTitle(t("reporting:people_analytics_title"), t("common:reports"));

  if (isChart && isEmployeeGrouping) {
    return (
      <Navigate replace to={`/reporting/employee/people-analytics?${searchParams.toString()}`} />
    );
  }

  return (
    <Stack direction="horizontal" className="h-full">
      <ConfigurePanel>
        <ConfigureSection label={t("reporting:people_analytics_group_people_by")}>
          <GroupBySelector
            catalog={catalog.data}
            groupByColumnId={groupByColumnId}
            onChange={handleGroupByChange}
            open={groupByOpen}
            onOpenChange={setGroupByOpen}
          />
        </ConfigureSection>
        <ConfigureSection label={t("reporting:people_analytics_visualisation")}>
          <VisualisationSelector
            isChart={isChart}
            chartType={chartType}
            chartDisabled={isEmployeeGrouping}
            onSelectTable={handleSelectTable}
            onSelectChart={handleSelectChart}
          />
        </ConfigureSection>
        <ConfigureSection label={t("reporting:people_analytics_show_metrics")}>
          <Stack gap="sm">
            {visibleCatalogColumns.map((column) => (
              <MetricCard
                key={column.id}
                column={column}
                title={metricTitles.get(column.id)}
                aggregation={resolveAggregation(column.id)}
                onChangeAggregation={(aggregation) =>
                  setMetrics(visibleColumnIds, { ...aggregations, [column.id]: aggregation })
                }
                onRemove={() =>
                  setMetrics(
                    visibleColumnIds.filter((id) => id !== column.id),
                    aggregations
                  )
                }
              />
            ))}
            <Button
              variant="plain"
              size="sm"
              className="justify-start px-0"
              leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
              onClick={handleAddMetric}
            >
              {t("reporting:people_analytics_add_metric")}
            </Button>
          </Stack>
        </ConfigureSection>
        <ConfigureSection label={t("reporting:people_analytics_filter")}>
          <Stack gap="sm">
            {filterCards.map((card) => (
              <FilterCard
                key={card.type}
                label={card.label}
                value={card.value}
                onEdit={() => openFilterField(card.type)}
                onRemove={() => removeFilterField(card)}
              />
            ))}
            {tableFilterCards.map((card) => (
              <FilterCard
                key={card.id}
                label={card.label}
                value={card.value}
                onEdit={() => openTableFilterField(card.id)}
                onRemove={() => removeTableFilterField(card.id)}
              />
            ))}
            <AddFilterMenu fields={availableFilterFields} onSelect={handleSelectFilterField} />
          </Stack>
        </ConfigureSection>
        <ConfigureSection label={t("reporting:people_analytics_period")}>
          <PeriodSelector timeScope={timeScope} onChange={handleTimeScopeChange} />
        </ConfigureSection>
      </ConfigurePanel>
      <Page
        className={
          isChart ? "bg-light h-full flex-1 min-w-0 overflow-y-auto" : "h-full flex-1 min-w-0"
        }
      >
        <Page.Header title={headerTitle} showBack={false}>
          <Stack
            direction="horizontal"
            gap="sm"
            items="center"
            className="overflow-hidden flex-wrap"
          >
            <PeopleAnalyticsCacheStatus />
            {isGroupByDate && (
              <DateGrainSelector dateGrain={dateGrain} onChange={handleDateGrainChange} />
            )}
            {hasMoneyMetric && (
              <DropdownMenu
                trigger={
                  <Button
                    variant="secondary"
                    rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-down" />}
                  >
                    {shownCurrencyTitle}
                  </Button>
                }
              >
                <Box onClick={(event) => event.stopPropagation()}>
                  <Input
                    label={t("common:search")}
                    placeholder={t("common:search_start_typing")}
                    value={currencySearch}
                    onChange={(event) => setCurrencySearch(event.target.value)}
                    onKeyDown={(event) => event.stopPropagation()}
                  />
                </Box>
                {currencyOptions
                  .filter((currency) =>
                    currency.title.toLowerCase().includes(currencySearch.toLowerCase())
                  )
                  .map((currency) => (
                    <DropdownMenuItem
                      key={currency.id}
                      title={currency.title}
                      isSelected={shownCurrency === currency.id}
                      onClick={() => setCurrency(currency.id)}
                    />
                  ))}
              </DropdownMenu>
            )}
            {canUpdateWidget && !isSaveDisabled ? (
              <>
                <SplitButton
                  variant="secondary"
                  leftIcon={<FontAwesomeIcon icon="fa-light fa-floppy-disk" />}
                  isLoading={updateWidget.isPending}
                  onClick={handleUpdateWidget}
                  menuLabel={t("common:more_save_options")}
                  menu={
                    <DropdownMenuItem
                      title={t("reporting:people_analytics_save_as_new")}
                      leftIcon={<FontAwesomeIcon icon="fa-light fa-copy" />}
                      onClick={handleSaveAs}
                    />
                  }
                >
                  {t("common:save")}
                </SplitButton>
                <DropdownMenu placement="bottom-end">
                  <DropdownMenuItem
                    title={t("reporting:people_analytics_rename")}
                    leftIcon={<FontAwesomeIcon icon="fa-light fa-pen" />}
                    onClick={handleRenameWidget}
                  />
                  <DropdownMenuItem
                    variant="menuDanger"
                    title={t("common:delete")}
                    leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />}
                    onClick={handleDeleteWidget}
                  />
                </DropdownMenu>
              </>
            ) : (
              <Tooltip delay={false} open={saveBlockedHint ? undefined : false}>
                <TooltipTrigger asChild>
                  <span>
                    <Button onClick={handleSaveAs} disabled={isSaveDisabled} variant="secondary">
                      {t("reporting:people_analytics_save_as")}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-60">{saveBlockedHint}</TooltipContent>
              </Tooltip>
            )}
            {canIEdit(ApiPermission.reporting_export) && (
              <Button onClick={handleExportClick} isLoading={isExporting} variant="primary">
                {t("common:export")}
              </Button>
            )}
          </Stack>
        </Page.Header>
        <Page.Content stretch={!isChart && !!groupByColumnId} className="pb-6">
          {catalog.isError || rows.isError ? (
            <EmptyStateServerError />
          ) : !groupByColumnId ? (
            <PeopleAnalyticsEmptyState onChooseField={() => setGroupByOpen(true)} />
          ) : isChart ? (
            <Stack gap="lg">
              <PeopleAnalyticsChart
                rows={rows.data}
                chartType={chartType}
                chartableColumns={chartableColumns}
                moneyColumnIds={moneyColumnIds}
                selectedColumnIds={seriesColumnIds}
                isLoading={catalog.isLoading || rows.isLoading}
                onPointClick={
                  isEmployeeGrouping
                    ? undefined
                    : (pointIndex) => openGroupEmployees(rows.data?.rows[pointIndex])
                }
                onToggleSeries={(columnId) => {
                  if (columnId === TOTAL_EMPLOYEES_SERIES_ID) {
                    setSeries([TOTAL_EMPLOYEES_SERIES_ID]);
                    return;
                  }
                  const metrics = seriesColumnIds.filter((id) => id !== TOTAL_EMPLOYEES_SERIES_ID);
                  const next = metrics.includes(columnId)
                    ? metrics.filter((id) => id !== columnId)
                    : [...metrics, columnId];
                  setSeries(next.length > 0 ? next : [TOTAL_EMPLOYEES_SERIES_ID]);
                }}
              />
              <Table<ApiPeopleAnalyticsRow>
                data={pagedRows.items}
                columns={columns}
                showSkeleton={catalog.isLoading || rows.isLoading}
                notFoundComponent={<EmptyStateNothingFound />}
                showNotFoundError={!rows.isLoading && rows.data?.rows.length === 0}
                onRowClick={
                  isEmployeeGrouping ? undefined : (row) => openGroupEmployees(row.original)
                }
              />
              <PaginationBlock metadata={pagedRows.metadata} onPageChange={handlePageChange} />
            </Stack>
          ) : (
            <Page.StickyLayout
              footer={
                <PaginationBlock metadata={pagedRows.metadata} onPageChange={handlePageChange} />
              }
            >
              <Table<ApiPeopleAnalyticsRow>
                data={pagedRows.items}
                columns={columns}
                showSkeleton={catalog.isLoading || rows.isLoading}
                stickyHeader
                stickyFirstColumn
                notFoundComponent={<EmptyStateNothingFound />}
                showNotFoundError={!rows.isLoading && rows.data?.rows.length === 0}
                onRowClick={
                  isEmployeeGrouping ? undefined : (row) => openGroupEmployees(row.original)
                }
              />
            </Page.StickyLayout>
          )}
        </Page.Content>
      </Page>
    </Stack>
  );
}
